import { Button } from '@/components/Button'
import { ErrorBlock } from '@/components/ErrorBlock'
import { Form, FormRow } from '@/components/Form'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentIllustration,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
} from '@/components/ModalSheet'
import { type ReactNode } from 'react'
import styles from './RemoveClientTeamDialog.module.css'
import { byDesign } from '@/infra/design'
import imgConfirmActionUrl from '@/assets/misc/img_confirm-action.svg'
import WorkspaceRolesIllustration from '@/components/SpotIllustration/WorkspaceRolesIllustration'

export interface RemoveClientTeamDialogProps {
  showing: boolean
  clientTeamField: ReactNode
  reasonField: ReactNode
  reasonHelpText: ReactNode
  isRemovePending: boolean
  isRemoveRejected: boolean
  removeError: unknown
  onClose: () => unknown
  onRemove: () => unknown
  closeButtonTestId?: string
  submitButtonTestId?: string
}

export function RemoveClientTeamDialog({
  showing,
  clientTeamField,
  reasonField,
  reasonHelpText,
  isRemovePending,
  isRemoveRejected,
  removeError,
  onClose,
  onRemove,
  closeButtonTestId,
  submitButtonTestId,
}: RemoveClientTeamDialogProps) {
  return (
    <ModalSheet
      showing={showing}
      onRequestClose={isRemovePending ? undefined : onClose}
      disableCloseOnEsc
      disableCloseOnOutsideClick
    >
      <ModalSheetHeader withCloseButton />

      <ModalSheetContent>
        <ModalSheetContentBlock>
          <ModalSheetContentIllustration
            src={imgConfirmActionUrl}
            illustration={byDesign(undefined, WorkspaceRolesIllustration)}
            alt="Illustration of a confused person with a question mark bubble above their head"
          />
          <ModalSheetContentTitle>
            Are you sure you want to remove the client team from this job?
          </ModalSheetContentTitle>

          <ModalSheetContentDescription>
            The removed team will no longer have access to this job.
          </ModalSheetContentDescription>

          <Form>
            <FormRow>
              <div className={styles.clientTeamLabel}>Client team</div>
              {clientTeamField}

              <div className={styles.reasonLabel}>Reason</div>
              {reasonField}
              <div className={styles.reasonHelp}>{reasonHelpText}</div>
            </FormRow>
          </Form>
        </ModalSheetContentBlock>
      </ModalSheetContent>

      {isRemoveRejected && <ErrorBlock tackOn="bottom" error={removeError} />}

      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Close"
            isDisabled={isRemovePending}
            variant="default"
            onPress={onClose}
            testId={closeButtonTestId}
          />
          <Button
            label="Remove"
            variant="destructive"
            isPending={isRemovePending}
            onPress={onRemove}
            testId={submitButtonTestId}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}
